import { z } from 'zod'
import { USER_DATE_YEAR_RANGE_LABEL } from '../constants/dates'
import { isUserYearInRange } from '../utils/user-date'
import { DATASHEET_URL_MAX, DATASHEET_URL_RE } from '../utils/customs'

// Управляющие ASCII-символы (кроме \t \n \r) — запрет в пользовательских строках.
// Закрывает A13-15: control chars в partNumber/brand/note.
// eslint-disable-next-line no-control-regex -- намеренно: регулярка запрещает control-chars на входе
const NO_CONTROL_CHARS = /^[^\x00-\x08\x0B\x0C\x0E-\x1F\x7F]*$/

const SAFE_STRING = (max: number) =>
  z.string().max(max).regex(NO_CONTROL_CHARS, 'Недопустимые управляющие символы')

// Разумные верхние границы (sanity bounds).
// Цена — до 1 млрд у.е. (превышение — ошибка ввода). Разрешён 0 — менеджер
// делает КП на все позиции, даже на те, по которым нет цены: непроквотированные
// уходят в КП с ценой 0.
// Количество — до 10 млн. Поддерживает дробные значения (0.1, 0.5) —
// в БД поле DOUBLE PRECISION; в типах TS — обычный JS number.
// Лид-тайм — 10 лет в днях.
const POSITIVE_PRICE = z.number().finite().nonnegative().max(1_000_000_000)
const POSITIVE_QTY = z.number().finite().positive().max(10_000_000)
const POSITIVE_DAYS = z.number().int().positive().max(3650)

// Единицы измерения, в которых разрешено дробное количество (0.5 кг, 12.3 м).
// Всё, чего нет в списке (включая null/пустое и 'шт', 'pcs') трактуется
// как «штучные» — количество должно быть целым.
const FRACTIONAL_UNITS = new Set(['л', 'кг', 'гр', 'г', 'м'])

export function isFractionalUnit(unit: string | null | undefined): boolean {
  if (!unit) return false
  return FRACTIONAL_UNITS.has(unit.trim().toLowerCase().replace(/\.$/, ''))
}

export function normalizeQuantityByUnit(qty: number, unit: string | null | undefined): number {
  if (!Number.isFinite(qty) || qty < 0) return 0
  if (isFractionalUnit(unit)) return Math.round(qty * 1000) / 1000
  // Штучные — округляем к ближайшему целому. 0.6 → 1, 0.4 → 0 (ошибка ввода).
  return Math.round(qty)
}

export const RequestTypeSchema = z.enum(['TEKUCHKA', 'PROJECT'])
// Держать в синхроне с константой RequestStatus (constants/statuses.ts) и
// Prisma-enum. QUOTATION_CREATED здесь отсутствовал (рассинхрон с 26.05) —
// CSV-фильтр statuses в GET /requests молча отбрасывал «КП создано».
export const RequestStatusSchema = z.enum([
  'DRAFT',
  'NEW',
  'IN_PROGRESS',
  'SENT_TO_PROCUREMENT',
  'PARTIALLY_PROCESSED',
  'QUOTATION_READY',
  'QUOTATION_CREATED',
  'QUOTE_SENT',
  'CLOSED_WON',
  'CLOSED_LOST',
  'ARCHIVED',
])
export const RequestItemPrioritySchema = z.enum(['HIGH', 'MEDIUM', 'LOW'])
export const RequestItemStatusSchema = z.enum([
  'NEW',
  'IN_QUEUE',
  'IN_PROGRESS',
  'NEEDS_INFO',
  'QUOTED',
  'PARTIAL',
  'READY_FOR_QUOTATION',
  'REWORK',
  'NOT_ACTUAL',
  'CANCELLED',
  'WORK_PROCUREMENT',
  'WORK_SALES',
  'KP_SENT',
  'TO_PROCUREMENT',
  'INVOICE',
  'ARCHIVED',
  'NO_OFFER',
  'REFUSED',
  'NO_RESPONSE',
])

// Метаданные парсинга черновика — исходный текст вставки и аннотированный
// результат распознавания (для восстановления «Результата распознавания» при
// переоткрытии черновика).
export const IntakeMetaSchema = z
  .object({
    rawText: z.string().max(200_000).optional(),
    parsedLines: z
      .array(
        z.object({
          line: z.string().max(2000),
          lineNum: z.number().int().nonnegative(),
          status: z.enum(['ok', 'warn', 'skipped']),
        }),
      )
      .max(5000)
      .optional(),
  })
  .nullable()

// Дедлайн заявки — общий для POST /requests и PATCH /requests/:id (№137, пакет 5).
// Раньше создание заявки год не проверяло вовсе (1999 и 3000 проходили, правка
// их отвергала). .datetime() отвергает расширенный ISO «+062026-…», который
// Prisma не принимает. refine проверяет год только у 4-значного формата, чтобы
// на «+062026» не выводить две ошибки подряд.
export const REQUEST_DEADLINE = z
  .string()
  .datetime({ message: 'некорректная дата' })
  .refine((s) => !/^\d{4}-/.test(s) || isUserYearInRange(Number(s.slice(0, 4))), {
    message: `год вне допустимого диапазона (${USER_DATE_YEAR_RANGE_LABEL}) — проверьте дату`,
  })

// Юрлицо-продавец (Р1): СПМ / ДИСТИНКТ / МОСЭЛ — коды SellerEntity из
// constants/orders.ts. Общая схема заявки (юрлицо продажи, с 23.09.2026) и счёта.
export const SellerEntitySchema = z.enum(['SPM', 'DISTINCT', 'MOSEL'])

export const CreateRequestSchema = z.object({
  clientId: z.string().uuid(),
  type: RequestTypeSchema,
  // Для DRAFT deadline может быть null — менеджер ещё не определился.
  // Для NEW/прочего — проверка на null делается в сервисе.
  deadline: REQUEST_DEADLINE.nullable().optional(),
  sourceChannel: SAFE_STRING(50).nullable().optional(),
  notes: SAFE_STRING(5000).nullable().optional(),
  // Признак «сохранить как черновик». На сервере → status=DRAFT и пропуск
  // валидации наличия deadline/позиций.
  asDraft: z.boolean().optional(),
  intakeMeta: IntakeMetaSchema.optional(),
  // Название проекта (Р12) — для заявок типа PROJECT; для текучки игнорируется.
  projectName: SAFE_STRING(120).nullable().optional(),
  // Юрлицо продажи (п. 16 встречи 17.09): не передано — СПЕЦМАШЭЛЕКТРО. Дальше
  // подставляется в КП и счёт.
  sellerEntityId: SellerEntitySchema.optional(),
  // Только для роли ASSISTANT (Помощник): менеджер-владелец заявки. Обязателен,
  // должен входить в прикреплённых менеджеров помощника. Остальные роли
  // игнорируются — владелец заявки всегда менеджер клиента.
  ownerManagerId: z.string().uuid().optional(),
})
export type CreateRequestDto = z.infer<typeof CreateRequestSchema>

export const CreateRequestItemSchema = z.object({
  partNumber: SAFE_STRING(200).min(1),
  brand: SAFE_STRING(100).nullable().optional(),
  quantity: POSITIVE_QTY,
  unit: SAFE_STRING(20).nullable().optional(),
  priority: RequestItemPrioritySchema.default('MEDIUM'),
  targetPrice: POSITIVE_PRICE.nullable().optional(),
  noteForProcurement: SAFE_STRING(5000).nullable().optional(),
  matrix: SAFE_STRING(50).nullable().optional(),
  category: SAFE_STRING(100).nullable().optional(),
  comment: SAFE_STRING(5000).nullable().optional(),
  // Даташит (нетарифка, 25.09.2026) — переносится при копировании заявки. Только
  // http(s); та же проверка, что DatasheetUrlSchema в customs.types (здесь своя —
  // customs.types сам импортирует этот файл).
  datasheetUrl: z
    .string()
    .trim()
    .max(DATASHEET_URL_MAX)
    .regex(DATASHEET_URL_RE, 'Ссылка на даташит должна начинаться с http:// или https://')
    .nullable()
    .optional(),
})
export type CreateRequestItemDto = z.infer<typeof CreateRequestItemSchema>

// Bulk-создание: смягчённая валидация, чтобы нераспознанные позиции попали
// в заявку и были подсвечены красным в таблице — менеджер исправит руками.
// partNumber — фронт подставляет "???" если парсер не распознал.
// quantity — 0 допустим, если количество не распознано.
export const BulkCreateRequestItemSchema = z.object({
  partNumber: SAFE_STRING(200).min(1),
  // Оригинальная строка заказчика (как прислал, без количества) — для колонки
  // «Артикул заказчика» в КП. Если не передан — бэк подставит partNumber.
  // Лимит 2000 = лимит строки парсера (rawLine): длинная строка письма —
  // легитимный ввод, отказывать 422 нельзя. Колонка в БД VarChar(200) —
  // бэк обрезает снапшот при записи (S500-3), см. bulk-create-order.ts.
  customerPartNumber: SAFE_STRING(2000).nullable().optional(),
  brand: SAFE_STRING(100).nullable().optional(),
  quantity: z.number().finite().min(0).max(10_000_000),
  unit: SAFE_STRING(20).nullable().optional(),
  priority: RequestItemPrioritySchema.default('MEDIUM'),
  targetPrice: POSITIVE_PRICE.nullable().optional(),
  noteForProcurement: SAFE_STRING(5000).nullable().optional(),
  matrix: SAFE_STRING(50).nullable().optional(),
  category: SAFE_STRING(100).nullable().optional(),
  comment: SAFE_STRING(5000).nullable().optional(),
})
export type BulkCreateRequestItemDto = z.infer<typeof BulkCreateRequestItemSchema>

export const BulkCreateRequestItemsSchema = z.object({
  items: z.array(BulkCreateRequestItemSchema).min(1).max(1000),
})
export type BulkCreateRequestItemsDto = z.infer<typeof BulkCreateRequestItemsSchema>

export const UpdateRequestItemSchema = z.object({
  status: RequestItemStatusSchema.optional(),
  assignedToId: z.string().uuid().nullable().optional(),
  noteForProcurement: SAFE_STRING(5000).nullable().optional(),
})
export type UpdateRequestItemDto = z.infer<typeof UpdateRequestItemSchema>

// Экспортируем sanity-bounds для переиспользования в api-схемах (procurement results и т.п.)
export { POSITIVE_PRICE, POSITIVE_QTY, POSITIVE_DAYS, SAFE_STRING }

/** Сколько картинок можно приложить к одному комментарию (с 29.10.2026). */
export const COMMENT_IMAGES_MAX = 5

export const CreateCommentSchema = z
  .object({
    // Пустой текст — только у комментария с картинками (проверка ниже).
    text: z.string().max(5000),
    requestId: z.string().uuid().optional(),
    requestItemId: z.string().uuid().optional(),
    // nullable() — фронт `ItemCommentsPanel` шлёт quoteId: null, когда
    // пользователь не выбрал привязку к конкретной квоте. До 30.04 это
    // ловилось как 422 «Expected string, received null».
    quoteId: z.string().uuid().nullable().optional(),
    // mentions — user-id адресатов @-меншена (выбраны из popup на фронте).
    // Если не пусто, на бэке создаётся узкое COMMENT_MENTION-уведомление,
    // широкая рассылка REQUEST_*_COMMENT_ADDED для этого комментария
    // подавляется. Лимит 20 — защита от случайного спама всем подряд.
    mentions: z.array(z.string().uuid()).max(20).optional(),
    // parentId — ответ на конкретный комментарий. Бэк проверяет, что родитель
    // относится к той же заявке/позиции (и к той же квоте, если есть quoteId),
    // и шлёт автору родителя уведомление COMMENT_REPLY (если он ≠ actor и не
    // попал в mentions — чтобы не дублировать).
    parentId: z.string().uuid().nullable().optional(),
    // Картинки, загруженные заранее (POST /comments/images), — по их id.
    imageIds: z.array(z.string().uuid()).max(COMMENT_IMAGES_MAX).optional(),
  })
  .refine((d) => (d.requestId != null) !== (d.requestItemId != null), {
    message: 'Нужно указать либо заявку, либо позицию',
  })
  .refine((d) => d.text.trim().length > 0 || (d.imageIds?.length ?? 0) > 0, {
    message: 'Напишите текст или приложите картинку',
    path: ['text'],
  })
export type CreateCommentDto = z.infer<typeof CreateCommentSchema>

// Редактирование собственного комментария. Меняется только текст и список
// @-меншенов. Автор фиксирован (RBAC на бэке), привязки (request/item/quote)
// не редактируются — нельзя «перепривязать» свой коммент к чужой заявке.
export const UpdateCommentSchema = z.object({
  // Пустой текст допустим только у комментария с картинками — проверяет сервис.
  text: z.string().max(5000),
  mentions: z.array(z.string().uuid()).max(20).optional(),
})
export type UpdateCommentDto = z.infer<typeof UpdateCommentSchema>
